import {
  Paper,
  Stack,
  Title,
  Avatar,
  Group,
  Text,
  SimpleGrid,
  ScrollArea,
  Divider,
  Button,
  UnstyledButton,
  Notification,
  Box,
} from '@mantine/core';
import { NavigationWrapper } from '../../../components/navigation/NavigationWrapper';
import {
  IconAlertCircle,
  IconMapPinFilled,
  IconUserCheck,
  IconUsers,
  IconUserPlus,
  IconChevronLeft,
  IconChevronRight,
  IconTagFilled,
  IconTagsFilled,
} from '@tabler/icons-react';
import { primaryColor } from '../../../constants/theme';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getProfessionalProfile,
  getProfessionalProfileByUsername,
} from '../../../services/ProfileService';
import { useApp } from '../../../context/AppContext';
import { ProfilePageSkeletonLoader } from '../components/profile/ProfilePageSkeletonLoader';
import { getProfilePicture } from '../../../types/UserDocument';
import { BookAppointmentsButton } from '../components/profile/BookAppointmentsButton';
import { SocialMediaButtons } from '../components/profile/SocialMediaButtons';
import { useEffect, useMemo, useState, useRef } from 'react';
import { EditProfileButton } from '../components/profile/EditProfileButton';
import { ShareProfileButton } from '../components/profile/ShareProfileButton';
import { PaymentsButton } from '../components/profile/PaymentsButton';
import { StripeStatusButton } from '../components/profile/StripePaymentButton';
import { ProfileCompletionBanner } from '../components/profile/ProfileCompletionBanner';
import { SimilarProfessionalsStack } from '../components/search/SearchResultsStack';
import { SearchProvider } from '../components/search/SearchContext';
import { ProfileResponseModel } from '../../../types/Profile';
import { PageRoutes } from '../../../constants/routes';
import {
  followCreator,
  unfollowCreator,
} from '../../../services/FollowingService';
import {
  setCreatorFollowStateInProfile,
} from '../../../services/followingProfile';

const FALLBACK_TEXT = 'Not specified';
const LOCATION_NOT_SET = '[Location not set]';
const SHOULD_SIMULATE_FOLLOW_FAILURE =
  import.meta.env.VITE_SIMULATE_FOLLOW_FAILURE === 'true';

const getLocationDisplayText = (profile: ProfileResponseModel) => {
  const city = profile?.city?.trim();

  if (city) {
    return city;
  }

  return profile?.isProfessional ? LOCATION_NOT_SET : 'Unknown';
};

const formatRate = (rate: number) => `£${rate}`;

const getPriceRangeText = (
  topics: NonNullable<ProfileResponseModel['activeTopics']>
): string | null => {
  if (!topics.length) return null;
  const rates = topics.map((t) => t.rate ?? 0);
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  if (min === max) return formatRate(min);
  return `${formatRate(min)} – ${formatRate(max)}`;
};

const CARD_WIDTH = 220;
const CARD_GAP = 16;
const SCROLL_AMOUNT = CARD_WIDTH + CARD_GAP;

interface TopicsCarouselProps {
  topics: NonNullable<ProfileResponseModel['activeTopics']>;
}

const TopicsCarousel = ({ topics }: TopicsCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Group justify="space-between" align="center" w="100%">
        <Title order={4} fw="normal">
          Topics
        </Title>
        <Group gap="xs">
          <UnstyledButton
            onClick={() => scroll('left')}
            aria-label="Scroll topics left"
          >
            <IconChevronLeft size={24} />
          </UnstyledButton>
          <UnstyledButton
            onClick={() => scroll('right')}
            aria-label="Scroll topics right"
          >
            <IconChevronRight size={24} />
          </UnstyledButton>
        </Group>
      </Group>
      <Box
        ref={scrollRef}
        style={{
          display: 'flex',
          gap: CARD_GAP,
          overflowX: 'auto',
          width: '100%',
          scrollBehavior: 'smooth',
        }}
      >
          {topics.map((topic) => (
            <Box
              key={topic.id}
              style={{
                backgroundColor: topic.colour ?? '#cccccc',
                borderRadius: 'var(--mantine-radius-md)',
                minWidth: CARD_WIDTH,
                maxWidth: CARD_WIDTH,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                height: 150,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Text
                fw={600}
                size="sm"
                c="white"
                ta="left"
                lineClamp={2}
                style={{ flex: '0 0 auto' }}
              >
                {topic.title}
              </Text>
              {topic.description && (
                <Text
                  size="xs"
                  c="white"
                  ta="left"
                  lineClamp={3}
                  style={{ flex: 1, marginTop: 4 }}
                >
                  {topic.description}
                </Text>
              )}
              <Group justify="space-between" mt="auto" pt={8}>
                <Text size="xs" c="white" fw={700}>
                  {topic.durationMinutes} min
                </Text>
                <Text size="xs" c="white" fw={700}>
                  {(topic.rate ?? 0) === 0 ? 'FREE' : `£${topic.rate}`}
                </Text>
              </Group>
            </Box>
          ))}
        </Box>
    </>
  );
};

export const ProfilePage = () => {
  const {
    scrollAreaHeight,
    profile: userProfile,
    isTabletAndBelow,
    onSetProfile,
  } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [optimisticFollowState, setOptimisticFollowState] =
    useState<boolean | null>(null);
  const [followErrorToast, setFollowErrorToast] = useState('');
  const professionalIdFromQuery = searchParams.get('professionalId');
  const profilePath = params['*'] ?? '';
  const usernameFromPath = useMemo(() => {
    if (!profilePath) return null;
    const [firstSegment] = profilePath.split('/').filter(Boolean);
    if (firstSegment?.startsWith('@')) {
      return firstSegment.substring(1);
    }
    return null;
  }, [profilePath]);

  const missingProfileIdentifier =
    !professionalIdFromQuery && !usernameFromPath;

  const {
    data: professionalFromUsername,
    isLoading: loadingUsernameLookup,
    isError: usernameLookupError,
    error: usernameLookupException,
  } = useQuery({
    queryKey: ['professionalProfileUsername', usernameFromPath],
    queryFn: () => getProfessionalProfileByUsername(usernameFromPath ?? ''),
    enabled: !!usernameFromPath && !professionalIdFromQuery,
  });

  const resolvedProfessionalId =
    professionalIdFromQuery ?? professionalFromUsername?.id ?? null;

  const isOwnProfile = useMemo(
    () => resolvedProfessionalId === userProfile?.id,
    [resolvedProfessionalId, userProfile]
  );

  const persistedIsFollowing = useMemo(() => {
    if (!resolvedProfessionalId || !userProfile) {
      return false;
    }

    return (userProfile.followedCreatorIds ?? []).includes(resolvedProfessionalId);
  }, [resolvedProfessionalId, userProfile]);

  const isFollowing = optimisticFollowState ?? persistedIsFollowing;

  useEffect(() => {
    setOptimisticFollowState(null);
  }, [resolvedProfessionalId]);

  useEffect(() => {
    if (!followErrorToast) {
      return;
    }

    const timeoutId = window.setTimeout(() => setFollowErrorToast(''), 3500);

    return () => window.clearTimeout(timeoutId);
  }, [followErrorToast]);

  const { mutate: mutateFollowState, isPending: followMutationPending } =
    useMutation({
      throwOnError: false,
      mutationFn: async ({
        professionalId,
        nextFollowState,
      }: {
        professionalId: string;
        nextFollowState: boolean;
      }) => {
        if (nextFollowState && SHOULD_SIMULATE_FOLLOW_FAILURE) {
          throw new Error('Simulated follow failure');
        }

        if (nextFollowState) {
          await followCreator(professionalId);
          return;
        }

        await unfollowCreator(professionalId);
      },
      onError: (_error, variables) => {
        if (!userProfile) {
          return;
        }

        const revertedProfile = setCreatorFollowStateInProfile(
          userProfile,
          variables.professionalId,
          !variables.nextFollowState
        );

        onSetProfile(revertedProfile);
        setOptimisticFollowState(!variables.nextFollowState);
        setFollowErrorToast(
          variables.nextFollowState
            ? 'Failed to follow creator. Please try again.'
            : 'Failed to unfollow creator. Please try again.'
        );
      },
      onSuccess: () => {
        setOptimisticFollowState(null);
      },
    });

  const handleFollowToggle = () => {
    if (!resolvedProfessionalId || !userProfile) {
      return;
    }

    const nextFollowState = !isFollowing;
    const updatedProfile = setCreatorFollowStateInProfile(
      userProfile,
      resolvedProfessionalId,
      nextFollowState
    );

    setOptimisticFollowState(nextFollowState);
    onSetProfile(updatedProfile);
    mutateFollowState({
      professionalId: resolvedProfessionalId,
      nextFollowState,
    });
  };

  const waitingOnUsernameResolution =
    !!usernameFromPath &&
    !professionalIdFromQuery &&
    !professionalFromUsername &&
    !usernameLookupError;

  const {
    data: profile,
    isLoading: loadingProfile,
    isError: errorLoadingProfile,
    error: profileError,
  } = useQuery({
    queryKey: ['professionalProfile', resolvedProfessionalId],
    queryFn: () => {
      return getProfessionalProfile(resolvedProfessionalId || '');
    },
    enabled: !!resolvedProfessionalId,
  });

  const showSkeleton = waitingOnUsernameResolution || loadingProfile;
  let blockingMessage: { title: string; body: string } | null = null;

  if (missingProfileIdentifier) {
    blockingMessage = {
      title: 'No profile selected',
      body: 'Use a shared profile link or search results to open a profile.',
    };
  } else if (usernameLookupError) {
    blockingMessage = {
      title: 'Unable to resolve username',
      body:
        (usernameLookupException as Error | undefined)?.message ??
        'Please check the shared link and try again.',
    };
  } else if (!waitingOnUsernameResolution && !resolvedProfessionalId) {
    blockingMessage = {
      title: 'Profile not found',
      body: 'We could not find a creator with that username.',
    };
  } else if (errorLoadingProfile && !profile) {
    blockingMessage = {
      title: 'Unable to load profile',
      body:
        (profileError as Error | undefined)?.message ??
        'Please refresh the page to try again.',
    };
  } else if (!profile && !showSkeleton) {
    blockingMessage = {
      title: 'Profile unavailable',
      body: 'This profile is temporarily unavailable. Please try again later.',
    };
  }

  const shownProfile =
    (isOwnProfile ? userProfile : profile) as ProfileResponseModel | undefined;

  const showFollowActions =
    !isOwnProfile &&
    !!shownProfile?.isProfessional &&
    !!resolvedProfessionalId;
  const websiteUrl = shownProfile?.websiteUrl?.trim();
  const followButton = (
    <Button
      variant="outline"
      color={primaryColor}
      leftSection={
        isFollowing ? <IconUserCheck size={18} /> : <IconUserPlus size={18} />
      }
      onClick={handleFollowToggle}
      loading={followMutationPending}
      data-testid="profile-follow-button"
    >
      {isFollowing ? 'Unfollow' : 'Follow'}
    </Button>
  );

  return (
    <NavigationWrapper disableScroll>
      <Group p={0} m={0} justify="center">
        <ScrollArea h={scrollAreaHeight} w={isTabletAndBelow ? '100%' : 850}>
          {showSkeleton ? (
            <ProfilePageSkeletonLoader isTabletOrBelow={isTabletAndBelow} />
          ) : blockingMessage ? (
            <Paper
              shadow="xl"
              radius="md"
              h={scrollAreaHeight}
              pt={20}
              mx="auto"
              mb={20}
            >
              <Stack
                align="center"
                justify="center"
                gap="xs"
                h={scrollAreaHeight}
              >
                <Title order={3}>{blockingMessage.title}</Title>
                <Text c="dimmed" ta="center" maw={480}>
                  {blockingMessage.body}
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Paper
              shadow="xl"
              radius="md"
              h={scrollAreaHeight}
              pt={20}
              mx="auto"
              mb={20}
            >
              <Stack bg="white" p="lg" align="start">
                {isTabletAndBelow ? (
                  <ProfileMobile
                    isOwnProfile={isOwnProfile}
                    profile={shownProfile as ProfileResponseModel}
                    onFollowingClick={() => navigate(PageRoutes.FOLLOWING)}
                  />
                ) : (
                  <ProfileDesktop
                    isOwnProfile={isOwnProfile}
                    profile={shownProfile as ProfileResponseModel}
                    onFollowingClick={() => navigate(PageRoutes.FOLLOWING)}
                  />
                )}
                {shownProfile && (
                  <ProfileCompletionBanner
                    profile={shownProfile}
                    isOwnProfile={isOwnProfile}
                  />
                )}
                <Group
                  gap="xs"
                  justify={isTabletAndBelow ? 'center' : 'start'}
                  style={{ alignItems: 'flex-start' }}
                  w={'100%'}
                >
                  {!isOwnProfile && <BookAppointmentsButton profile={profile} />}
                  {(isOwnProfile
                    ? userProfile?.isProfessional
                    : profile?.isProfessional) && (
                    <ShareProfileButton username={profile?.username ?? ''} />
                  )}
                  {showFollowActions && followButton}
                  {isOwnProfile && (
                    <>
                      <EditProfileButton />
                      <PaymentsButton />
                      <StripeStatusButton />
                    </>
                  )}
                </Group>
                {shownProfile?.isProfessional && (
                  <>
                    {shownProfile?.activeTopics?.length ? (
                      <TopicsCarousel topics={shownProfile.activeTopics} />
                    ) : (
                      <>
                        <Title order={4} fw="normal">
                          Topics
                        </Title>
                        <Text c="dimmed">No topics available</Text>
                      </>
                    )}
                  </>
                )}
                <Title order={4} fw="normal">
                  Bio
                </Title>
                <Text c="dimmed" ta="start">
                  {profile?.headerBio ?? 'No bio available'}
                </Text>
                {!shownProfile?.isProfessional && profile?.about && (
                  <>
                    <Title order={4} fw="normal">
                      About
                    </Title>
                    <Text c="dimmed" ta="start">
                      {profile?.about}
                    </Text>
                  </>
                )}
                {websiteUrl && (
                  <>
                    <Title order={4} fw="normal">
                      Website Url
                    </Title>
                    <Text
                      c={primaryColor}
                      ta="start"
                      component="a"
                      href={websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {websiteUrl}
                    </Text>
                  </>
                )}
                <Title order={4} fw="normal">
                  Socials
                </Title>
                <SocialMediaButtons profile={profile} />
                <SimpleGrid
                  cols={{ base: 1, sm: 2, lg: 3 }}
                  spacing="lg"
                  mt="md"
                  w="100%"
                >
                  <div>
                    <Title order={4} fw="normal">
                      Languages
                    </Title>
                    <Text c="dimmed" ta="start">
                      {profile?.userLanguages?.length
                        ? profile.userLanguages
                            .map((lang) => lang.name)
                            .join(', ')
                        : FALLBACK_TEXT}
                    </Text>
                  </div>
                  {profile?.isProfessional && (
                    <div>
                      <Title order={4} fw="normal">
                        Categories
                      </Title>
                      <Text c="dimmed" ta="start">
                        {profile.userCategories.length
                          ? profile.userCategories
                              .map((cat) => cat.name)
                              .join(', ')
                          : FALLBACK_TEXT}
                      </Text>
                    </div>
                  )}
                  {profile?.isProfessional && (
                    <div>
                      <Title order={4} fw="normal">
                        Search Terms
                      </Title>
                      <Text c="dimmed" ta="start">
                        {profile.userSearchTerms?.length
                          ? profile.userSearchTerms
                              .map((term) => term.name)
                              .join(', ')
                          : FALLBACK_TEXT}
                      </Text>
                    </div>
                  )}
                </SimpleGrid>
              </Stack>
            </Paper>
          )}
        </ScrollArea>
        {!isTabletAndBelow && (
          <ScrollArea h={scrollAreaHeight}>
            <Paper
              shadow="xl"
              w="350px"
              mx="auto"
              radius="md"
              mih={scrollAreaHeight}
              mb={20}
            >
              <SearchProvider>
                <SimilarProfessionalsStack
                  categories={
                    profile?.userCategories.map((cat) => cat.name) || []
                  }
                  currentProfessionalId={resolvedProfessionalId ?? ''}
                />
              </SearchProvider>
            </Paper>
          </ScrollArea>
        )}
      </Group>
      {followErrorToast && (
        <Notification
          color="red"
          title="Action failed"
          withCloseButton
          onClose={() => setFollowErrorToast('')}
          icon={<IconAlertCircle size={16} />}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 400,
          }}
          data-testid="profile-follow-error-toast"
        >
          {followErrorToast}
        </Notification>
      )}
    </NavigationWrapper>
  );
};

export const ProfileDesktop = ({
  profile,
  isOwnProfile,
  onFollowingClick,
}: {
  profile: ProfileResponseModel;
  isOwnProfile: boolean;
  onFollowingClick: () => void;
}) => {
  return (
    <Group align="start" wrap="nowrap" w="100%">
      <Avatar
        size={125}
        src={getProfilePicture(profile?.profilePicture)}
      ></Avatar>
      <Stack gap={5} style={{ flex: 1 }}>
        <Title order={3} fw="normal">
          {profile.isProfessional ? profile?.displayName : profile?.fullName}
        </Title>
        <Text fz="md" c="dimmed" ta="start">
          {profile?.headline}
        </Text>
        <Group>
          <IconMapPinFilled color={primaryColor} size={16} />
          <Text c="dimmed">{getLocationDisplayText(profile)}</Text>
          {(() => {
            const priceRange = getPriceRangeText(profile?.activeTopics ?? []);
            return priceRange ? (
              <>
                <IconTagFilled color={primaryColor} size={16} />
                <Text c="dimmed" data-testid="profile-price-range">
                  {priceRange}
                </Text>
              </>
            ) : null;
          })()}
        </Group>
        {isOwnProfile && (profile?.following ?? 0) > 0 && (
          <UnstyledButton onClick={onFollowingClick} data-testid="profile-following-summary">
            <Group gap={6}>
              <IconUsers
                color={primaryColor}
                fill={primaryColor}
                size={16}
                stroke={1.5}
              />
              <Text c={primaryColor}>{profile.following} following</Text>
            </Group>
          </UnstyledButton>
        )}
      </Stack>
    </Group>
  );
};

export const ProfileMobile = ({
  profile,
  isOwnProfile,
  onFollowingClick,
}: {
  profile: ProfileResponseModel;
  isOwnProfile: boolean;
  onFollowingClick: () => void;
}) => {
  return (
    <>
      <Stack justify="center" align="center" w="100%" gap={2}>
        <Avatar
          size={125}
          src={getProfilePicture(profile?.profilePicture)}
        ></Avatar>
        <Title order={3} fw="normal">
          {profile.isProfessional ?  profile.displayName : profile?.fullName}
        </Title>
        <Text fz="md" c="dimmed" ta="center">
          {profile?.headline}
        </Text>
        <Group>
          <IconMapPinFilled color={primaryColor} size={16} />
          <Text c="dimmed">{getLocationDisplayText(profile)}</Text>
          {(() => {
            const priceRange = getPriceRangeText(profile?.activeTopics ?? []);
            return priceRange ? (
              <>
                <IconTagsFilled color={primaryColor} size={16} />
                <Text c="dimmed" data-testid="profile-price-range">
                  {priceRange}
                </Text>
              </>
            ) : null;
          })()}
        </Group>
        {isOwnProfile && (profile?.following ?? 0) > 0 && (
          <UnstyledButton onClick={onFollowingClick} data-testid="profile-following-summary">
            <Group gap={6}>
              <IconUsers
                color={primaryColor}
                fill={primaryColor}
                size={16}
                stroke={1.5}
              />
              <Text c={primaryColor}>{profile.following} following</Text>
            </Group>
          </UnstyledButton>
        )}
      </Stack>
      <Divider w="100%" />
    </>
  );
};
